import React, { useState, useEffect } from 'react';
import { X, CreditCard, Lock, AlertCircle, CheckCircle, Loader } from 'lucide-react';

/**
 * Payment Form Component
 * Securely collects payment information and creates Maxio subscription
 * Uses Chargify.js for PCI-compliant card tokenization
 */
const PaymentForm = ({ isOpen, onClose, plan }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    billingAddress: '',
    billingCity: '',
    billingState: '',
    billingZip: '',
    billingCountry: 'US'
  });

  const [cardData, setCardData] = useState({
    number: '',
    month: '',
    year: '',
    cvv: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chargifyLoaded, setChargifyLoaded] = useState(false);

  // Load Chargify.js script
  useEffect(() => {
    if (!window.Chargify) {
      const script = document.createElement('script');
      script.src = 'https://js.chargify.com/latest/chargify.js';
      script.async = true;
      script.onload = () => {
        console.log('Chargify.js loaded successfully');
        setChargifyLoaded(true);
      };
      script.onerror = () => {
        setError('Failed to load payment processor. Please refresh the page.');
      };
      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    } else {
      setChargifyLoaded(true);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
  };

  const handleCardChange = (e) => {
    const { name, value } = e.target;

    // Basic input formatting
    let formattedValue = value;

    if (name === 'number') {
      // Remove non-digits and limit to 16 characters
      formattedValue = value.replace(/\D/g, '').slice(0, 16);
    } else if (name === 'month') {
      // Limit to 2 digits, validate 01-12
      formattedValue = value.replace(/\D/g, '').slice(0, 2);
    } else if (name === 'year') {
      // Limit to 4 digits
      formattedValue = value.replace(/\D/g, '').slice(0, 4);
    } else if (name === 'cvv') {
      // Limit to 4 digits (Amex can have 4)
      formattedValue = value.replace(/\D/g, '').slice(0, 4);
    }

    setCardData(prev => ({
      ...prev,
      [name]: formattedValue
    }));
    setError(null);
  };

  const validateForm = () => {
    // Validate customer info
    if (!formData.firstName.trim()) return 'First name is required';
    if (!formData.lastName.trim()) return 'Last name is required';
    if (!formData.email.trim()) return 'Email is required';
    if (!/\S+@\S+\.\S+/.test(formData.email)) return 'Invalid email address';

    // Validate billing info
    if (!formData.billingAddress.trim()) return 'Billing address is required';
    if (!formData.billingCity.trim()) return 'City is required';
    if (!formData.billingState.trim()) return 'State is required';
    if (!formData.billingZip.trim()) return 'ZIP code is required';

    // Validate card info
    if (!cardData.number) return 'Card number is required';
    if (cardData.number.length < 13) return 'Invalid card number';
    if (!cardData.month) return 'Expiration month is required';
    if (!cardData.year) return 'Expiration year is required';
    if (!cardData.cvv) return 'CVV is required';
    if (cardData.cvv.length < 3) return 'Invalid CVV';

    // Validate expiration date
    const month = parseInt(cardData.month);
    const year = parseInt(cardData.year);
    if (month < 1 || month > 12) return 'Invalid expiration month';
    if (year < new Date().getFullYear()) return 'Card has expired';

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    if (!chargifyLoaded) {
      setError('Payment processor not loaded. Please refresh the page.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Initialize Chargify
      const chargify = new window.Chargify();

      // Get Chargify public key from environment
      const publicKey = process.env.REACT_APP_CHARGIFY_PUBLIC_KEY;
      if (!publicKey) {
        throw new Error('Payment processor not configured');
      }

      // Tokenize the card using Chargify.js
      console.log('Tokenizing card with Chargify...');
      const tokenData = await new Promise((resolve, reject) => {
        chargify.token(
          {
            serverHost: `https://${process.env.REACT_APP_MAXIO_SUBDOMAIN}.chargify.com`,
            publicKey: publicKey,
            type: 'card',
            firstName: formData.firstName,
            lastName: formData.lastName,
            number: cardData.number,
            month: cardData.month,
            year: cardData.year,
            cvv: cardData.cvv
          },
          (token) => {
            if (token) {
              resolve(token);
            } else {
              reject(new Error('Failed to tokenize card'));
            }
          },
          (error) => {
            reject(new Error(error.message || 'Card tokenization failed'));
          }
        );
      });

      console.log('Card tokenized successfully');

      // Create subscription via our backend API
      const response = await fetch('/api/create-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          productHandle: plan.productHandle,
          paymentToken: tokenData,
          billingAddress: formData.billingAddress,
          billingCity: formData.billingCity,
          billingState: formData.billingState,
          billingZip: formData.billingZip,
          billingCountry: formData.billingCountry
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || result.error || 'Payment failed');
      }

      console.log('Subscription created successfully:', result);

      // Redirect to success page
      window.location.href = result.redirectUrl;

    } catch (error) {
      console.error('Payment error:', error);
      setError(error.message || 'Payment failed. Please try again.');
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-red-600 p-6 rounded-t-2xl relative">
          <button
            onClick={onClose}
            disabled={loading}
            className="absolute top-4 right-4 text-white hover:bg-white/20 rounded-full p-2 transition"
          >
            <X className="w-6 h-6" />
          </button>
          <h2 className="text-3xl font-black text-white mb-2">Complete Your Purchase</h2>
          <p className="text-white/90 font-semibold">
            {plan.name} Plan - {plan.price} {plan.period}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border-2 border-red-500 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-800 font-semibold">{error}</p>
            </div>
          )}

          {/* Customer Information */}
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Customer Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  disabled={loading}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none disabled:bg-gray-100"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  disabled={loading}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none disabled:bg-gray-100"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                disabled={loading}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none disabled:bg-gray-100"
                required
              />
            </div>
          </div>

          {/* Billing Address */}
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-gray-800">Billing Address</h3>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Street Address *
              </label>
              <input
                type="text"
                name="billingAddress"
                value={formData.billingAddress}
                onChange={handleInputChange}
                disabled={loading}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none disabled:bg-gray-100"
                required
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  City *
                </label>
                <input
                  type="text"
                  name="billingCity"
                  value={formData.billingCity}
                  onChange={handleInputChange}
                  disabled={loading}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none disabled:bg-gray-100"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  State *
                </label>
                <input
                  type="text"
                  name="billingState"
                  value={formData.billingState}
                  onChange={handleInputChange}
                  disabled={loading}
                  maxLength="2"
                  placeholder="CA"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none disabled:bg-gray-100 uppercase"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  ZIP Code *
                </label>
                <input
                  type="text"
                  name="billingZip"
                  value={formData.billingZip}
                  onChange={handleInputChange}
                  disabled={loading}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none disabled:bg-gray-100"
                  required
                />
              </div>
            </div>
          </div>

          {/* Card Information */}
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <Lock className="w-5 h-5 text-green-600" />
              Payment Information
            </h3>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Card Number *
              </label>
              <input
                type="text"
                name="number"
                value={cardData.number}
                onChange={handleCardChange}
                disabled={loading}
                placeholder="1234 5678 9012 3456"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none disabled:bg-gray-100 font-mono"
                required
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Month *
                </label>
                <input
                  type="text"
                  name="month"
                  value={cardData.month}
                  onChange={handleCardChange}
                  disabled={loading}
                  placeholder="MM"
                  maxLength="2"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none disabled:bg-gray-100 text-center font-mono"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Year *
                </label>
                <input
                  type="text"
                  name="year"
                  value={cardData.year}
                  onChange={handleCardChange}
                  disabled={loading}
                  placeholder="YYYY"
                  maxLength="4"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none disabled:bg-gray-100 text-center font-mono"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  CVV *
                </label>
                <input
                  type="text"
                  name="cvv"
                  value={cardData.cvv}
                  onChange={handleCardChange}
                  disabled={loading}
                  placeholder="123"
                  maxLength="4"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none disabled:bg-gray-100 text-center font-mono"
                  required
                />
              </div>
            </div>
          </div>

          {/* Security Notice */}
          <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 flex items-start gap-3">
            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-green-800 font-semibold">
                Your payment information is secure
              </p>
              <p className="text-green-700 text-sm mt-1">
                Card details are encrypted and never stored on our servers. Processed securely through Maxio/Chargify.
              </p>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || !chargifyLoaded}
            className="w-full px-6 py-4 bg-gradient-to-r from-blue-600 to-red-600 text-white text-xl font-black rounded-xl hover:from-blue-700 hover:to-red-700 transition shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
          >
            {loading ? (
              <>
                <Loader className="w-6 h-6 animate-spin" />
                Processing Payment...
              </>
            ) : !chargifyLoaded ? (
              'Loading Payment Processor...'
            ) : (
              <>
                Complete Purchase - {plan.price}
                <Lock className="w-5 h-5" />
              </>
            )}
          </button>

          <p className="text-center text-gray-500 text-sm">
            By completing this purchase, you agree to our terms of service.
          </p>
        </form>
      </div>
    </div>
  );
};

export default PaymentForm;
